import { Injectable, OnDestroy } from "@angular/core";
import { BreakpointObserver } from "@angular/cdk/layout";
import { Subscription, combineLatest } from "rxjs";
import { ZoomService } from "@app/common/action-menu/zoom/zoom.service";

@Injectable({ providedIn: "root" })
export class CardScaleService implements OnDestroy {
  private sub = new Subscription();

  // Must match the breakpoints in bc-theme.scss :root media queries (Phase 1 fallback).
  // Bases are px, not rem: html/body font-size escalates 16→17→18→20px across
  // breakpoints (see base.scss), so a rem value here would silently inflate card
  // size at each breakpoint. px keeps these anchored to the documented targets.
  private readonly deviceBreakpoints = [
    { query: "(max-width: 767px)",                                base: 11.200 }, // → 84×133px
    { query: "(max-width: 1199px) and (orientation: portrait)",  base: 12.928 }, // → 97×154px
    { query: "(max-width: 1199px) and (orientation: landscape)", base: 14.656 }, // → 110×175px
  ];
  private readonly defaultBase = 12.924; // desktop → 97×153px (75% of the previous 129×205px)

  constructor(
    private breakpointObserver: BreakpointObserver,
    private zoomService: ZoomService
  ) {
    const queries = this.deviceBreakpoints.map((b) => b.query);

    this.sub.add(
      combineLatest([
        this.breakpointObserver.observe(queries),
        this.zoomService.zoomLevel$,
      ]).subscribe(([bpState, zoomPct]) => {
        const match = this.deviceBreakpoints.find((b) => bpState.breakpoints[b.query]);
        const base = match?.base ?? this.defaultBase;
        const fs = (base * zoomPct / 100).toFixed(4);
        const basefs = base.toFixed(4);
        document.documentElement.style.setProperty("--card-font-size",      `${fs}px`);
        document.documentElement.style.setProperty("--card-font-size-base", `${basefs}px`);
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
